/**
 * DevServerConnector — process management service connector.
 *
 * Manages dev server processes (Vite, Nuxt, Next, etc.) with port allocation,
 * health checks, log capture, and optional git worktree isolation.
 *
 * Category: service
 */

import type {
  ConnectContext,
  ConnectorDeclaration,
  ConnectorHandle,
  OperationDescriptor,
  RuntimeSkillDescriptor,
  ToolFace,
} from "@skaile/workspaces/connectors";
import {
  AbstractConnector,
  createWorktree,
  findGitRoot,
  LogBuffer,
  PortPool,
} from "@skaile/workspaces/connectors";
import { portableSpawn } from "@skaile/workspaces/core";

// ── Internal types ───────────────────────────────────────────────────────────

interface ServiceInstance {
  id: string;
  pid: number;
  port: number;
  cwd: string;
  status: "starting" | "healthy" | "unhealthy" | "stopped" | "crashed";
  startedAt: string;
  command?: string;
  env?: Record<string, string>;
  ref?: string;
  worktreePath?: string;
  worktreeCleanup?: () => Promise<void>;
  healthEndpoint?: string;
  managed: boolean;
  restartable: boolean;
  process?: { pid: number; kill(signal?: number): void; exited: Promise<number> };
  logBuffer?: LogBuffer;
}

interface DevServerState {
  instances: Map<string, ServiceInstance>;
  pool: PortPool;
  repoRoot: string | null;
  worktreeDir: string;
  logBufferSize: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function isPidAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch {
    return false;
  }
}

async function pipeToBuffer(stream: ReadableStream<Uint8Array>, buf: LogBuffer): Promise<void> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf.pushLines(decoder.decode(value));
    }
  } catch {
    /* stream closed */
  }
}

let idCounter = 0;

// ── Connector ────────────────────────────────────────────────────────────────

/**
 * Connector for managing dev server processes — spawns, tracks, and stops processes with port allocation, health checks, and log capture.
 * Supports optional git worktree isolation for per-instance code snapshots.
 * @docLink packages/factory-assets/concepts#devserver-connector
 */
export class DevServerConnector extends AbstractConnector {
  readonly name = "devserver";

  tools: ToolFace;

  constructor() {
    super();
    this.tools = {
      describeOperations: (handle) => this._describeOperations(handle),
      executeOp: (handle, operation, args) => this._executeOp(handle, operation, args),
      describeSkill: (handle, declaration) => this._describeSkill(handle, declaration),
    };
  }

  async connect(declaration: ConnectorDeclaration, _ctx: ConnectContext): Promise<ConnectorHandle> {
    this.log.info("connect", { id: declaration.id });
    const opts = declaration.options ?? {};
    const portRange = (opts.port_range as [number, number]) ?? [3000, 3099];
    let repoRoot: string | null = null;
    try {
      repoRoot = await findGitRoot(process.cwd());
    } catch {}

    const state: DevServerState = {
      instances: new Map(),
      pool: new PortPool(portRange),
      repoRoot,
      worktreeDir: String(opts.worktree_dir ?? ".skaile/worktrees"),
      logBufferSize: Number(opts.log_buffer_size ?? 500),
    };

    return this.makeHandle(declaration, state);
  }

  async disconnect(handle: ConnectorHandle): Promise<void> {
    this.log.info("disconnect");
    try {
      await this._stopAll(handle);
    } finally {
      handle.status = "disconnected";
    }
  }

  override async onSessionClose(handle: ConnectorHandle): Promise<string | undefined> {
    await this._stopAll(handle);
    return undefined;
  }

  override async onHibernate(handle: ConnectorHandle): Promise<string | undefined> {
    const state = handle.state as DevServerState;
    await this._stopAll(handle);
    return `Stopped ${state.instances.size} dev server instances for hibernation`;
  }

  // ── Tool face methods (private, wired in constructor) ─────────────────────

  private async _stopAll(handle: ConnectorHandle): Promise<void> {
    const state = handle.state as DevServerState;
    for (const [id, inst] of [...state.instances.entries()]) {
      if (inst.managed && inst.process) await this._killProcess(inst, state);
      state.instances.delete(id);
    }
  }

  private async _killProcess(inst: ServiceInstance, state: DevServerState): Promise<void> {
    if (!inst.process) return;
    try {
      inst.process.kill(15);
    } catch {}
    const exited = await Promise.race([
      inst.process.exited.then(() => true),
      new Promise<false>((r) => setTimeout(() => r(false), 5000)),
    ]);
    if (!exited) {
      try {
        inst.process.kill(9);
      } catch {}
    }
    inst.status = "stopped";
    state.pool.release(inst.port);
    if (inst.worktreeCleanup) {
      try {
        await inst.worktreeCleanup();
      } catch {}
    }
  }

  private _describeOperations(_handle: ConnectorHandle): OperationDescriptor[] {
    return [
      {
        name: "start",
        description: "Spawn a dev server process.",
        accessLevel: "write",
        args: [
          { name: "command", type: "string", required: true, description: "Shell command to run" },
          { name: "cwd", type: "string", required: false, description: "Working directory" },
          {
            name: "ref",
            type: "string",
            required: false,
            description: "Git ref for worktree isolation",
          },
          { name: "port", type: "number", required: false, description: "Explicit port" },
          { name: "label", type: "string", required: false, description: "Instance label" },
          {
            name: "env",
            type: "json",
            required: false,
            description: "Additional environment variables",
          },
          {
            name: "health_endpoint",
            type: "string",
            required: false,
            description: "Health check URL",
          },
        ],
        examples: ['start --command "bun run dev" --label "frontend"'],
      },
      {
        name: "register",
        description: "Track an externally started process.",
        accessLevel: "write",
        args: [
          { name: "pid", type: "number", required: true, description: "Process ID" },
          { name: "port", type: "number", required: true, description: "Port" },
          { name: "label", type: "string", required: false, description: "Instance label" },
          { name: "cwd", type: "string", required: false, description: "Working directory" },
          {
            name: "health_endpoint",
            type: "string",
            required: false,
            description: "Health check URL",
          },
        ],
      },
      {
        name: "stop",
        description: "Stop a dev server instance.",
        accessLevel: "write",
        args: [{ name: "instance", type: "string", required: true, description: "Instance ID" }],
      },
      {
        name: "restart",
        description: "Restart a managed instance.",
        accessLevel: "write",
        args: [{ name: "instance", type: "string", required: true, description: "Instance ID" }],
      },
      {
        name: "status",
        description: "Check instance health.",
        accessLevel: "read",
        args: [
          {
            name: "instance",
            type: "string",
            required: false,
            description: "Instance ID (omit for all)",
          },
        ],
      },
      {
        name: "logs",
        description: "Get recent stdout/stderr.",
        accessLevel: "read",
        args: [
          { name: "instance", type: "string", required: true, description: "Instance ID" },
          {
            name: "lines",
            type: "number",
            required: false,
            description: "Number of lines (default: 50)",
          },
        ],
      },
      {
        name: "list_instances",
        description: "List all tracked instances.",
        accessLevel: "read",
        args: [],
      },
      {
        name: "free_port",
        description: "Allocate a port from the pool.",
        accessLevel: "read",
        args: [],
      },
    ];
  }

  private async _executeOp(
    handle: ConnectorHandle,
    operation: string,
    args: Record<string, unknown>,
  ): Promise<string> {
    const state = handle.state as DevServerState;
    if (
      ["start", "register", "stop", "restart"].includes(operation) &&
      handle.access === "read-only"
    ) {
      throw new Error(`DevServerConnector: "${operation}" requires read-write access`);
    }

    switch (operation) {
      case "start":
        return this._opStart(handle, state, args);
      case "register":
        return this._opRegister(state, args);
      case "stop":
        return this._opStop(state, args);
      case "restart":
        return this._opRestart(handle, state, args);
      case "status":
        return this._opStatus(state, args);
      case "logs":
        return this._opLogs(state, args);
      case "list_instances":
        return this._opListInstances(state);
      case "free_port":
        return this._opFreePort(state);
      default:
        throw new Error(`DevServerConnector: unknown operation "${operation}"`);
    }
  }

  private async _opStart(
    handle: ConnectorHandle,
    state: DevServerState,
    args: Record<string, unknown>,
  ): Promise<string> {
    const command = args.command ? String(args.command) : undefined;
    if (!command) throw new Error("start requires a command argument");
    let cwd = args.cwd ? String(args.cwd) : process.cwd();
    const ref = args.ref ? String(args.ref) : undefined;
    const label = args.label ? String(args.label) : `srv-${++idCounter}`;
    const userEnv = (args.env as Record<string, string>) ?? {};
    const healthEndpoint = args.health_endpoint ? String(args.health_endpoint) : undefined;

    let worktreePath: string | undefined;
    let worktreeCleanup: (() => Promise<void>) | undefined;
    if (ref) {
      if (!state.repoRoot) throw new Error("start with ref requires a git repository");
      const wt = await createWorktree({
        repoRoot: state.repoRoot,
        ref,
        baseDir: state.worktreeDir,
        label,
        owner: handle.id,
      });
      cwd = wt.path;
      worktreePath = wt.path;
      worktreeCleanup = wt.cleanup;
    }

    let port: number;
    if (args.port !== undefined && args.port !== null) {
      port = Number(args.port);
      if (!(await PortPool.isPortFree(port))) throw new Error(`Port ${port} is already in use`);
      state.pool.reserve(port);
    } else {
      port = await state.pool.allocate();
    }

    const resolvedHealth = healthEndpoint
      ? healthEndpoint.replace(/\bPORT\b/g, String(port))
      : `http://localhost:${port}/`;
    this.log.info("spawn dev server", { label, command, cwd, port });
    const proc = portableSpawn(["sh", "-c", command], {
      cwd,
      env: { ...process.env, ...userEnv, PORT: String(port) },
      detached: true,
    });
    const logBuffer = new LogBuffer(state.logBufferSize);
    // `void`: these pumps run for the process's lifetime and must not be
    // awaited here. `pipeToBuffer` swallows its own stream errors, so neither
    // can reject.
    void pipeToBuffer(proc.stdout, logBuffer);
    void pipeToBuffer(proc.stderr, logBuffer);

    const instance: ServiceInstance = {
      id: label,
      pid: proc.pid,
      port,
      cwd,
      status: "starting",
      startedAt: new Date().toISOString(),
      command,
      env: userEnv,
      ref,
      worktreePath,
      worktreeCleanup,
      healthEndpoint: resolvedHealth,
      managed: true,
      restartable: true,
      process: proc,
      logBuffer,
    };
    state.instances.set(label, instance);
    void proc.exited.then((code: number | null) => {
      this.log.info("dev server exited", { label, exitCode: code });
      if (instance.status !== "stopped") {
        instance.status = code === 0 ? "stopped" : "crashed";
        if (code !== 0 && code !== null) {
          this.log.warn("dev server crashed", { label, exitCode: code });
        }
        this.emitChange?.({
          path: `instance/${instance.id}`,
          action: "delete",
          source: "operation",
        });
      }
    });

    return JSON.stringify({ instance_id: label, pid: proc.pid, port, cwd });
  }

  private async _opRegister(state: DevServerState, args: Record<string, unknown>): Promise<string> {
    const pid = Number(args.pid);
    const port = Number(args.port);
    if (!pid || Number.isNaN(pid)) throw new Error("register requires a valid pid");
    if (!port || Number.isNaN(port)) throw new Error("register requires a valid port");
    if (!isPidAlive(pid)) throw new Error(`PID ${pid} is not alive`);
    if (await PortPool.isPortFree(port)) throw new Error(`Port ${port} is not bound`);

    const label = args.label ? String(args.label) : `ext-${pid}`;
    state.pool.reserve(port);
    state.instances.set(label, {
      id: label,
      pid,
      port,
      cwd: args.cwd ? String(args.cwd) : process.cwd(),
      status: "healthy",
      startedAt: new Date().toISOString(),
      healthEndpoint: args.health_endpoint
        ? String(args.health_endpoint)
        : `http://localhost:${port}/`,
      managed: false,
      restartable: false,
    });
    return JSON.stringify({ instance_id: label });
  }

  private async _opStop(state: DevServerState, args: Record<string, unknown>): Promise<string> {
    const id = String(args.instance ?? "");
    const inst = state.instances.get(id);
    if (!inst) throw new Error(`Instance "${id}" not found`);
    if (inst.managed && inst.process) await this._killProcess(inst, state);
    else state.pool.release(inst.port);
    state.instances.delete(id);
    this.emitChange?.({ path: `instance/${id}`, action: "delete", source: "operation" });
    return JSON.stringify({ stopped: true });
  }

  private async _opRestart(
    handle: ConnectorHandle,
    state: DevServerState,
    args: Record<string, unknown>,
  ): Promise<string> {
    const id = String(args.instance ?? "");
    const inst = state.instances.get(id);
    if (!inst) throw new Error(`Instance "${id}" not found`);
    if (!inst.managed || !inst.restartable) throw new Error(`Instance "${id}" is not restartable`);
    const { command, cwd, env, port, healthEndpoint } = inst;
    if (inst.process) await this._killProcess(inst, state);
    state.instances.delete(id);
    state.pool.reserve(port);
    return this._opStart(handle, state, {
      command,
      cwd,
      env,
      port,
      label: id,
      health_endpoint: healthEndpoint,
    });
  }

  private async _opStatus(state: DevServerState, args: Record<string, unknown>): Promise<string> {
    if (args.instance) {
      const inst = state.instances.get(String(args.instance));
      if (!inst) throw new Error(`Instance "${args.instance}" not found`);
      await this._refreshStatus(inst);
      return JSON.stringify(instanceToStatus(inst));
    }
    for (const inst of state.instances.values()) await this._refreshStatus(inst);
    return JSON.stringify([...state.instances.values()].map(instanceToStatus));
  }

  private async _refreshStatus(inst: ServiceInstance): Promise<void> {
    if (inst.status === "stopped") return;
    if (!isPidAlive(inst.pid)) {
      inst.status = "crashed";
      return;
    }
    if (inst.healthEndpoint) {
      try {
        const res = await fetch(inst.healthEndpoint, { signal: AbortSignal.timeout(3000) });
        inst.status = res.ok ? "healthy" : "unhealthy";
      } catch {
        inst.status = "unhealthy";
      }
    } else {
      inst.status = "healthy";
    }
  }

  private _opLogs(state: DevServerState, args: Record<string, unknown>): string {
    const inst = state.instances.get(String(args.instance ?? ""));
    if (!inst) throw new Error(`Instance "${args.instance}" not found`);
    if (!inst.managed) throw new Error(`Cannot retrieve logs for non-managed instance`);
    return inst.logBuffer!.tail(args.lines ? Number(args.lines) : 50).join("\n");
  }

  private _opListInstances(state: DevServerState): string {
    return JSON.stringify(
      [...state.instances.values()].map((i) => ({
        id: i.id,
        pid: i.pid,
        port: i.port,
        status: i.status,
        ref: i.ref,
        managed: i.managed,
        started_at: i.startedAt,
        cwd: i.cwd,
      })),
    );
  }

  private async _opFreePort(state: DevServerState): Promise<string> {
    return JSON.stringify({ port: await state.pool.allocate() });
  }

  private _describeSkill(
    handle: ConnectorHandle,
    declaration: ConnectorDeclaration,
  ): RuntimeSkillDescriptor {
    return {
      id: declaration.id,
      name: `Dev Server — ${declaration.id}`,
      description:
        "Manage dev server processes with port allocation, health checks, and log capture.",
      category: "service",
      access: declaration.access,
      toolNames: this._describeOperations(handle).map((o) => o.name),
    };
  }
}

function instanceToStatus(inst: ServiceInstance) {
  return {
    id: inst.id,
    pid: inst.pid,
    port: inst.port,
    status: inst.status,
    managed: inst.managed,
    restartable: inst.restartable,
    ref: inst.ref,
    cwd: inst.cwd,
    started_at: inst.startedAt,
    health_endpoint: inst.healthEndpoint,
    uptime_ms: Date.now() - new Date(inst.startedAt).getTime(),
  };
}

/**
 * Creates a new DevServerConnector instance.
 * @returns Configured DevServerConnector ready to be registered in the connector registry.
 * @docLink packages/factory-assets/api-reference#devserver-connector-factory
 */
export function createConnector(): DevServerConnector {
  return new DevServerConnector();
}
